import Spinner from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { endpoints } from "../../../hooks/endpoints";
import {
  getAllowedCategoriesForPermission,
  getToken,
} from "../../../hooks/utils";
import { useAppSelector } from "../../../store/hooks";
import Separator from "../../Separator";
import CodeLessonsAndCats from "./CodeLessonsAndCats";

export interface CodesLessonsAndCatsI {
  [key: string]: {
    id: string;
    title: string;
    slug?: string;
    parent?: number;
    included: boolean;
    excluded: boolean;
  };
}

const EditCode = () => {
  const [codeInfo, setCodeInfo] = useState(null);
  const [isCodeLoading, setIsCodeLoading] = useState(false);
  const [isCodeEdiLoading, setIsCodeEditLoading] = useState(false);
  const [error, setError] = useState(null);
  const codeRef = useRef(null);

  const { portal } = useAppSelector((state) => state.store);

  const allowedCatsForCodes =
    getAllowedCategoriesForPermission("can_create_codes");

  const portalCats = allowedCatsForCodes
    ? portal?.cats?.filter((cat) => allowedCatsForCodes.includes(cat.term_id))
    : portal?.cats;

  const portalLessons = allowedCatsForCodes
    ? portal?.data?.filter((lesson) =>
        allowedCatsForCodes?.includes(Number(lesson.term_taxonomy_id))
      )
    : portal?.data;

  const [lessons, setLessons] = useState<CodesLessonsAndCatsI>({});
  const [cats, setCats] = useState<CodesLessonsAndCatsI>({});

  const handleCheckCode = async (e) => {
    try {
      e.preventDefault();
      setIsCodeLoading(true);
      setError(null);

      const res = await fetch(
        endpoints.checkCode +
          `?code=${codeRef.current?.value}&timestamp=${new Date().getTime()}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: getToken(),
          },
          method: "GET",
        }
      );
      const response = await res.json();

      if (!res.ok) {
        throw new Error(response.data.message);
      }

      setCodeInfo(response);

      // Lessons
      const initialLessons = {};
      portalLessons?.forEach((el) => {
        initialLessons[el.ID] = {
          id: el.ID,
          title: el.post_title,
          slug: el.slug,
          included: response.data.products.includes(Number(el.ID)),
          excluded: response.data.excluded_products.includes(Number(el.ID)),
        };
      });
      setLessons(initialLessons);

      // Cats
      const initialCats = {};
      portalCats?.forEach(
        (el) =>
          (initialCats[el.term_id] = {
            id: el.term_id,
            title: el.name?.split("|")[0],
            slug: el.slug,
            parent: el.parent,
            included: response.data.categories.includes(+el.term_id),
            excluded: response.data.excluded_categories.includes(+el.term_id),
          })
      );
      setCats(initialCats);
    } catch (e) {
      setError(e.message);
      setCodeInfo(null);
    } finally {
      setIsCodeLoading(false);
    }
  };

  const handleUpdateCode = async (e) => {
    e.preventDefault();
    setIsCodeEditLoading(true);
    setError(null);

    try {
      const res = await fetch(endpoints.updateCode, {
        headers: {
          "Content-Type": "application/json",
          Authorization: getToken(),
        },
        method: "POST",
        body: JSON.stringify({
          code: codeRef.current?.value,
          included_lessons: Object.keys(lessons).filter(
            (el) => lessons[el].included
          ),
          included_cats: Object.keys(cats).filter((el) => cats[el].included),
          excluded_lessons: Object.keys(lessons).filter(
            (el) => lessons[el].excluded
          ),
          excluded_cats: Object.keys(cats).filter((el) => cats[el].excluded),
        }),
      });

      const response = await res.json();
      if (!res.ok) {
        throw new Error(response.message);
      }
      toast.success("تم تعديل الكود بنجاح");
    } catch (e) {
      setError(e.message);
    } finally {
      setIsCodeEditLoading(false);
    }
  };

  return (
    <form
      className="p-3 rounded-lg border shadow-sm bg-white"
      onSubmit={() => {}}
    >
      <h4 className="p-2 mb-4 border-b-2 border-primary w-fit font-bold">
        تعديل كود
      </h4>

      <div className="mb-3">
        <input
          ref={codeRef}
          type="text"
          id="exampleFormControlInput1"
          placeholder="أكتب الكود هنا..."
          className="border border-gray-300 rounded-md p-2 w-full focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      <Button
        className="rounded-lg px-4 py-2"
        disabled={isCodeLoading}
        type="button"
        onClick={handleCheckCode}
      >
        {!isCodeLoading ? "بحث" : <Spinner variant="button" />}
      </Button>

      {codeInfo?.data?.discount_type === "percent" ? (
        <>
          <Separator />
          <CodeLessonsAndCats
            type="included"
            itemType="lesson"
            title="حصص يمكن فتحها بالكود"
            items={lessons}
            setItems={setLessons}
          />
          <Separator />
          <CodeLessonsAndCats
            type="included"
            itemType="category"
            title="مجاميع يمكن فتح حصصها بالكود"
            items={cats}
            setItems={setCats}
          />
          <Separator />
          <CodeLessonsAndCats
            type="excluded"
            itemType="lesson"
            title="حصص لا يمكن فتحها بالكود"
            items={lessons}
            setItems={setLessons}
          />
          <Separator />
          <CodeLessonsAndCats
            type="excluded"
            itemType="category"
            title="مجاميع لا يمكن فتح حصصها بالكود"
            items={cats}
            setItems={setCats}
          />
          <Button
            className="rounded-lg px-4 py-2 mt-3"
            disabled={isCodeEdiLoading}
            type="button"
            onClick={handleUpdateCode}
          >
            {!isCodeEdiLoading ? "تعديل" : <Spinner variant="button" />}
          </Button>
        </>
      ) : codeInfo?.data?.discount_type === "fixed_cart" ? (
        <p className="mt-3 p-2 text-red-500">
          هذا الكود خاص بالرصيد و لا يمكن تعديله
        </p>
      ) : (
        <></>
      )}

      {!!error && (
        <div
          className="bg-red-100 text-red-700 rounded-lg px-4 py-2 my-2 w-fit"
          role="alert"
        >
          {error}
        </div>
      )}
    </form>
  );
};

export default EditCode;
